import { formatDate } from '@backoffice-frontend/common'
import { CheckCircleIcon } from '@moia-dev/pace-icons'
import { Badge, Text } from '@moia-dev/pace-web'
import { useTranslation } from 'react-i18next'
import { AnnotationJiraLink } from './AnnotationJiraLink'
import { annotationEvidenceTypeLabel } from './annotation-labels'
import type { AnnotationResolution } from './types'

export type AnnotationResolvedBadgeProps = {
  resolution: AnnotationResolution
}

/**
 * The "Resolved" tag for a note whose issue was fixed (trip-manager#1135),
 * with the evidence behind it: what kind (Jira issue, software release, map
 * version), the reference itself, and when.
 *
 * The evidence sits beside the tag rather than in a tooltip, because the popup
 * this lives in is itself a hover popup. A resolution is only as good as what
 * backs it, so a reviewer should not have to dig for it.
 *
 * `resolvedBy` is left out: trip-manager stores the resolver's OpenID `sub`, a
 * UUID that means nothing to a reader.
 *
 * Exported so trip history's Annotations tab tags its rows the same way.
 */
export const AnnotationResolvedBadge = ({
  resolution,
}: AnnotationResolvedBadgeProps) => {
  const { t } = useTranslation()
  const evidenceTypeLabel = annotationEvidenceTypeLabel(
    t,
    resolution.evidenceType,
  )

  return (
    <div
      css={theme => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space.Space1,
        minWidth: 0,
      })}
    >
      <div
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: theme.space.Space2,
          rowGap: theme.space.Space1,
        })}
      >
        <Badge
          variant="success"
          icon={<CheckCircleIcon />}
          label={t('mapAnnotations_resolution_badge')}
        />
        <Text size="sm" color="reduced" css={{ overflowWrap: 'anywhere' }}>
          {resolution.evidenceType === 'jira_issue' ? (
            <>
              {evidenceTypeLabel}{' '}
              <AnnotationJiraLink issueKey={resolution.evidenceRef} size="sm" />
            </>
          ) : evidenceTypeLabel ? (
            `${evidenceTypeLabel} ${resolution.evidenceRef}`
          ) : (
            resolution.evidenceRef
          )}
          {' · '}
          {formatDate(new Date(resolution.resolvedAtMs))}
        </Text>
      </div>
      {resolution.note ? (
        <Text size="sm" color="reduced" css={{ overflowWrap: 'anywhere' }}>
          {resolution.note}
        </Text>
      ) : null}
    </div>
  )
}
